import { useState, useEffect } from 'react';
import { Wrench, AlertTriangle, Calendar } from 'lucide-react';
import toast from 'react-hot-toast';
import { getGlobalSettings, toggleMaintenanceMode } from 'wasp/client/operations';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import { Alert, AlertDescription, AlertTitle } from '../../components/ui/alert';

type MaintenanceSettings = {
  enabled: boolean;
  message: string;
  scheduledStart?: string;
  scheduledEnd?: string;
  enabledBy?: string;
  enabledAt?: string;
};

const MaintenanceModeTab = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [enabled, setEnabled] = useState(false);
  const [message, setMessage] = useState(
    'The platform is currently undergoing maintenance. Please check back soon.'
  );
  const [scheduledStart, setScheduledStart] = useState('');
  const [scheduledEnd, setScheduledEnd] = useState('');

  useEffect(() => {
    loadMaintenanceSettings();
  }, []);

  const loadMaintenanceSettings = async () => {
    try {
      setLoading(true);
      const settings = await getGlobalSettings();
      const maintenanceSetting = settings.find((s: any) => s.settingKey === 'maintenance_mode');

      if (maintenanceSetting) {
        const value = maintenanceSetting.settingValue as MaintenanceSettings;
        setEnabled(value.enabled || false);
        setMessage(
          value.message ||
            'The platform is currently undergoing maintenance. Please check back soon.'
        );
        setScheduledStart(value.scheduledStart || '');
        setScheduledEnd(value.scheduledEnd || '');
      }
    } catch (error: any) {
      toast.error(error.message || 'Failed to load maintenance settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);

      await toggleMaintenanceMode({
        enabled,
        message,
        scheduledStart: scheduledStart ? new Date(scheduledStart) : undefined,
        scheduledEnd: scheduledEnd ? new Date(scheduledEnd) : undefined
      });

      toast.success(
        enabled
          ? 'Maintenance mode enabled successfully'
          : 'Maintenance mode disabled successfully'
      );
    } catch (error: any) {
      toast.error(error.message || 'Failed to update maintenance mode');
    } finally {
      setSaving(false);
    }
  };

  const handleQuickToggle = async () => {
    const newEnabled = !enabled;
    setEnabled(newEnabled);

    try {
      setSaving(true);

      await toggleMaintenanceMode({
        enabled: newEnabled,
        message,
        scheduledStart: scheduledStart ? new Date(scheduledStart) : undefined,
        scheduledEnd: scheduledEnd ? new Date(scheduledEnd) : undefined
      });

      toast.success(
        newEnabled
          ? 'Maintenance mode enabled successfully'
          : 'Maintenance mode disabled successfully'
      );
    } catch (error: any) {
      toast.error(error.message || 'Failed to toggle maintenance mode');
      // Revert on error
      setEnabled(!newEnabled);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="py-8 text-center">
        <p className="text-muted-foreground">Loading maintenance mode settings...</p>
      </div>
    );
  }

  return (
    <div className="w-full">
      <div className="px-6 py-6 space-y-6">
        {/* Status Alert */}
        {enabled && (
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle>Maintenance Mode is Active</AlertTitle>
            <AlertDescription>
              The platform is currently in maintenance mode. Regular users cannot access the site.
            </AlertDescription>
          </Alert>
        )}

        {/* Quick Toggle */}
        <div className="pb-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Wrench className="h-5 w-5" />
              <h3 className="font-semibold leading-none tracking-tight">Maintenance Mode</h3>
            </div>
            <Switch
              checked={enabled}
              onCheckedChange={handleQuickToggle}
              disabled={saving}
              className="ml-auto"
            />
          </div>
          <p className="text-sm text-muted-foreground mt-1.5">
            Enable maintenance mode to prevent regular users from accessing the platform
          </p>
        </div>

        {/* Settings */}
        <div>
          <div className="mb-4">
            <h3 className="font-semibold leading-none tracking-tight">Maintenance Settings</h3>
            <p className="text-sm text-muted-foreground mt-1.5">
              Configure the maintenance message and schedule
            </p>
          </div>
          <div className="space-y-6">
            {/* Maintenance Message */}
            <div className="space-y-2">
              <Label htmlFor="maintenance-message">Maintenance Message</Label>
              <Textarea
                id="maintenance-message"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Enter the message users will see during maintenance..."
                rows={4}
                className="resize-none"
              />
              <p className="text-sm text-muted-foreground">
                This message will be displayed to users when they try to access the platform
              </p>
            </div>

            {/* Scheduled Maintenance */}
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4 text-muted-foreground" />
                <Label className="text-base font-semibold">Scheduled Maintenance (Optional)</Label>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="scheduled-start">Start Date & Time</Label>
                  <Input
                    id="scheduled-start"
                    type="datetime-local"
                    value={scheduledStart}
                    onChange={(e) => setScheduledStart(e.target.value)}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="scheduled-end">End Date & Time</Label>
                  <Input
                    id="scheduled-end"
                    type="datetime-local"
                    value={scheduledEnd}
                    onChange={(e) => setScheduledEnd(e.target.value)}
                  />
                </div>
              </div>

              <p className="text-sm text-muted-foreground">
                Set a schedule to automatically enable and disable maintenance mode. Leave empty for manual control.
              </p>
            </div>

            {/* Save Button */}
            <div className="flex justify-end gap-4 pt-4 border-t border-gray-200 dark:border-gray-700">
              <Button variant="outline" onClick={loadMaintenanceSettings} disabled={saving}>
                Reset
              </Button>
              <Button onClick={handleSave} disabled={saving}>
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          </div>
        </div>

        {/* Info */}
        <div className="p-4 bg-muted rounded-lg">
          <h4 className="font-medium mb-2">Important Notes</h4>
          <ul className="space-y-2 text-sm text-muted-foreground">
            <li className="flex gap-2">
              <span className="text-primary">•</span>
              <span>
                <strong>Administrators and Platform Owners</strong> can always access the platform, even during maintenance mode
              </span>
            </li>
            <li className="flex gap-2">
              <span className="text-primary">•</span>
              <span>
                <strong>Regular users</strong> will see the maintenance message and cannot log in or access any pages
              </span>
            </li>
            <li className="flex gap-2">
              <span className="text-primary">•</span>
              <span>
                <strong>Scheduled maintenance</strong> will automatically toggle maintenance mode on/off at the specified times (feature requires backend cron job)
              </span>
            </li>
            <li className="flex gap-2">
              <span className="text-primary">•</span>
              <span>
                <strong>API access</strong> will also be restricted during maintenance mode for non-admin users
              </span>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default MaintenanceModeTab;



